import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  TextCallout2,
  TextCaption1,
  TextCaption3,
  Button as BuiButton,
  Link as BuiLink,
  colors,
} from '@blocket/blocket-ui'
import getParsedData from 'components/pagesContent/Chat/utils/getParsedData'
import replaceSpacesInAmount from 'components/pagesContent/Chat/utils/replaceSpacesInAmount'
import getChatButtonType from 'components/pagesContent/Chat/utils/getChatButtonType'
import { trackingExtensionIntegrationCTA } from 'tracking/chat'
import { awaitPulseTracking } from 'utils/pulse'
import ExtensionIcon from '../../../../shared/ExtensionIcon'

const ExtensionItem = styled.li`
  width: 100%;
  margin-bottom: 16px;
  flex: 0 0 auto;
`

const Wrapper = styled.div`
  padding: 16px;
  border: 1px solid ${colors.grey400};
  border-radius: 6px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const HeaderText = styled(TextCaption1)`
  margin-left: 8px;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  & > * {
    margin-bottom: 16px;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`

const ContentText = styled(TextCallout2)`
  font-size: 17px;
  line-height: 26px;
  word-break: break-word;
`

const Time = styled(TextCaption3)`
  margin-bottom: 8px;
  width: 100%;
  text-align: center;
`

const Link = styled(BuiLink)`
  font-size: 15px;
  line-height: 20px;
  align-self: ${({ isMobile }) => (isMobile ? 'center' : 'flex-start')};
  text-decoration: underline;
  word-break: break-all;
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`

const Button = styled(BuiButton)`
  width: 100%;
  min-width: 0;
  ${({ isMobile }) => !isMobile && 'max-width: 280px'};

  &:first-child {
    ${({ addMarginRight }) => addMarginRight && 'margin-right: 16px'};
    ${({ addMarginBottom }) => addMarginBottom && 'margin-bottom: 16px'};
  }

  & a {
    overflow: hidden;
    text-align: center;
    & span {
      display: inline-block;
      justify-content: flex-start;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`

const Extension = ({ jsonData, currentUserId, date, isMe, selectedChannel, isMobile }) => {
  const extensionData = getParsedData(jsonData)
  const users = extensionData?.extension?.users
  const userExtension =
    Array.isArray(users) &&
    users.find((user) => {
      return user.sb_user_id === currentUserId
    })

  const usersOwnAd = getParsedData(selectedChannel.data)?.ad?.sb_user_id === currentUserId
  const role = usersOwnAd ? 'seller' : 'buyer'

  const extensionButtonClick = useCallback(
    (e, extensionAction) => {
      e.preventDefault()

      awaitPulseTracking(
        () =>
          trackingExtensionIntegrationCTA({
            extensionData,
            extensionAction,
            selectedChannel,
            role,
          }),
        () => (window.location = `${extensionAction.url}&mc_callback_url=${window.location.href}`),
      )
    },
    [extensionData, selectedChannel, role],
  )

  if (!userExtension?.content || !extensionData) {
    return null
  }

  const { header, text, text2, image_url, link, label } = userExtension?.content || {}
  const actionArray = userExtension.actions || []
  const hasButtons = actionArray.length > 0
  const twoButtons = actionArray.length === 2

  const extensionButtons = actionArray.map((extensionAction, index) => {
    const key = index
    return (
      <Button
        key={key}
        type={getChatButtonType(extensionAction.type)}
        href={`${extensionAction.url}&mc_callback_url=${window.location.href}`}
        size="small"
        onClick={(e) => extensionButtonClick(e, extensionAction)}
        isMobile={isMobile}
        addMarginRight={!isMobile && twoButtons}
        addMarginBottom={isMobile && twoButtons}
      >
        {extensionAction.label}
      </Button>
    )
  })

  return (
    <ExtensionItem>
      <Time isMe={isMe}>{date}</Time>
      <Wrapper>
        <Header>
          <ExtensionIcon iconSrc={image_url} width={32} height={32} />
          {header && <HeaderText>{header.toUpperCase()}</HeaderText>}
        </Header>
        <Content>
          {text && <ContentText>{replaceSpacesInAmount(text)}</ContentText>}
          {text2 && <ContentText>{replaceSpacesInAmount(text2)}</ContentText>}
          {label && link && (
            <Link href={link} isMobile={isMobile}>
              {label}
            </Link>
          )}
          {hasButtons && <ButtonWrapper isMobile={isMobile}>{extensionButtons}</ButtonWrapper>}
        </Content>
      </Wrapper>
    </ExtensionItem>
  )
}

Extension.propTypes = {
  // Message data (data: https://docs.sendbird.com/platform/messages#_4_resource_representation).
  jsonData: PropTypes.string.isRequired,
  // Current user Sendbird ID (user_id: https://docs.sendbird.com/platform/user#_4_resource_representation).
  currentUserId: PropTypes.string.isRequired,
  // Message createdAt (created_at: https://docs.sendbird.com/platform/messages#_4_resource_representation).
  date: PropTypes.string,
  // State if the message was sent from the current user.
  isMe: PropTypes.bool,
  // Current user selected channel (https://docs.sendbird.com/platform/group_channel#_4_resource_representation).
  selectedChannel: PropTypes.object.isRequired,
  // State if current device is a mobile.
  isMobile: PropTypes.bool.isRequired,
}

export default Extension
